import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ZapIcon, CrownIcon, SwordsIcon } from 'lucide-react';
interface PricingTier {
  name: string;
  price: string;
  description: string;
  features: string[];
  cta: string;
  icon: React.ElementType;
  popular?: boolean;
}
export function Pricing() {
  const tiers: PricingTier[] = [{
    name: 'Recruit',
    price: '€9',
    description: 'Perfect for starting squads',
    icon: SwordsIcon,
    features: ['1 Team Slot', 'Up to 5 Players', 'Basic Tournament Brackets', 'Team Profile Page', 'Community Discord Access'],
    cta: 'Enlist Now'
  }, {
    name: 'Captain',
    price: '€29',
    description: 'For competitive teams',
    icon: ZapIcon,
    popular: true,
    features: ['3 Team Slots', 'Up to 15 Players', 'Jersey Builder Access', 'Advanced Brackets (Double Elim, Swiss)', 'Match Analytics Dashboard', 'Priority Matchmaking'],
    cta: 'Take Command'
  }, {
    name: 'Warlord',
    price: '€79',
    description: 'For esports organizations',
    icon: CrownIcon,
    features: ['Unlimited Teams', 'Unlimited Players', 'Full Jersey Builder + Merch Store', 'All Tournament Formats', 'Advanced Analytics + Scouting', 'Dedicated Account Manager', 'API Access'],
    cta: 'Conquer All'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut'
      }
    }
  };
  return <section id="pricing" className="relative py-24 sm:py-32 overflow-hidden" data-id="element-152">
      <div className="absolute inset-0 bg-gray-950 bg-dot-pattern opacity-50" data-id="element-153" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-154">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-155">
          <h2 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-white mb-4" data-id="element-156">
            CHOOSE YOUR{' '}
            <span className="text-cyan-400 text-glow-cyan" data-id="element-157">LOADOUT</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto" data-id="element-158">
            Scale from a 5-stack to a full competitive organization.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true
      }} className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch" data-id="element-159">
          {tiers.map(tier => <motion.div key={tier.name} variants={cardVariants} className={`relative flex flex-col bg-gray-900/80 backdrop-blur-sm rounded-2xl overflow-hidden ${tier.popular ? 'border-2 border-cyan-500 glow-cyan scale-105 z-10' : 'border border-gray-800'}`} data-id="element-160">
              {/* Popular Badge */}
              {tier.popular && <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-cyan-500 to-cyan-400 text-gray-950 text-xs font-bold uppercase tracking-wider text-center py-1.5" data-id="element-161">
                  Most Popular
                </div>}

              <div className={`p-8 flex-1 flex flex-col ${tier.popular ? 'pt-12' : ''}`} data-id="element-162">
                {/* Icon & Name */}
                <div className="flex items-center gap-3 mb-4" data-id="element-163">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${tier.popular ? 'bg-cyan-500/20 border border-cyan-500/50' : 'bg-gray-800 border border-gray-700'}`} data-id="element-164">
                    <tier.icon className={`w-6 h-6 ${tier.popular ? 'text-cyan-400' : 'text-gray-400'}`} data-id="element-165" />
                  </div>
                  <div data-id="element-166">
                    <h3 className="font-display font-bold text-xl text-white uppercase tracking-wide" data-id="element-167">
                      {tier.name}
                    </h3>
                    <p className="text-sm text-gray-500" data-id="element-168">{tier.description}</p>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-6" data-id="element-169">
                  <span className={`font-display font-black text-5xl ${tier.popular ? 'text-cyan-400' : 'text-white'}`} data-id="element-170">
                    {tier.price}
                  </span>
                  <span className="text-gray-500 text-lg" data-id="element-171">/mo</span>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1" data-id="element-172">
                  {tier.features.map(feature => <li key={feature} className="flex items-start gap-3" data-id="element-173">
                      <CheckIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${tier.popular ? 'text-cyan-400' : 'text-lime-400'}`} data-id="element-174" />
                      <span className="text-gray-300" data-id="element-175">{feature}</span>
                    </li>)}
                </ul>

                {/* CTA */}
                <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} className={`w-full py-4 font-bold text-sm uppercase tracking-wider rounded-lg transition-colors ${tier.popular ? 'bg-cyan-500 text-gray-950 hover:bg-cyan-400 glow-cyan' : 'bg-gray-800 text-white border border-gray-700 hover:border-cyan-500/50 hover:bg-gray-800/80'}`} data-id="element-176">
                  {tier.cta}
                </motion.button>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Additional Info */}
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="text-center text-gray-500 mt-12" data-id="element-177">
          All plans include a 14-day free trial. No credit card required. Cancel
          anytime.
        </motion.p>
      </div>
    </section>;
}